import { useState } from "react";
import { mockBookings } from "../../../mocks/accountData";

const statusConfig: Record<string, { label: string; color: string; dot: string }> = {
  completed: {
    label: "Completed",
    color: "bg-green-100 text-green-700 border-green-200",
    dot: "bg-green-500",
  },
  upcoming: {
    label: "Upcoming",
    color: "bg-cl-lime/40 text-cl-dark border-cl-lime",
    dot: "bg-cl-green",
  },
  cancelled: {
    label: "Cancelled",
    color: "bg-red-100 text-red-600 border-red-200",
    dot: "bg-red-400",
  },
};

export default function BookingsTab() {
  const [filter, setFilter] = useState<"all" | "upcoming" | "completed">("all");

  const filtered = mockBookings.filter((b) => {
    if (filter === "all") return true;
    return b.status === filter;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black text-cl-dark">My Bookings</h2>
          <p className="text-gray-500 text-sm mt-0.5">{mockBookings.length} collections on record</p>
        </div>
        <Link
          to="/book"
          className="bg-cl-dark text-white font-black text-sm px-5 py-2.5 rounded-xl cartoon-btn whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          Book Again
        </Link>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {(["all", "upcoming", "completed"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all cursor-pointer capitalize whitespace-nowrap ${
              filter === f
                ? "bg-white text-cl-dark thick-border cartoon-shadow-sm"
                : "text-gray-500 hover:text-cl-dark"
            }`}
          >
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Booking cards */}
      <div className="flex flex-col gap-4">
        {filtered.map((booking) => {
          const status = statusConfig[booking.status] ?? statusConfig.completed;
          return (
            <div
              key={booking.id}
              className="bg-white rounded-2xl thick-border cartoon-shadow p-5 hover:shadow-none transition-all"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-dark flex-shrink-0">
                    <i className={`${booking.icon} text-cl-dark text-lg`}></i>
                  </span>
                  <div>
                    <p className="font-black text-cl-dark text-base leading-tight">{booking.service}</p>
                    <p className="text-gray-400 text-xs font-semibold">{booking.id}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${status.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                  {status.label}
                </span>
              </div>

              {/* Meta row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <i className="ri-calendar-line text-cl-green"></i>
                  <span className="font-semibold">{booking.date}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <i className="ri-time-line text-cl-green"></i>
                  <span className="font-semibold">{booking.timeSlot}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <i className="ri-map-pin-line text-cl-green"></i>
                  <span className="font-semibold truncate">{booking.address.split(",")[0]}</span>
                </div>
              </div>

              {/* Items */}
              <div className="flex flex-wrap gap-2 mb-4">
                {booking.items.map((item) => (
                  <span
                    key={item}
                    className="bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full border border-transparent"
                  >
                    {item}
                  </span>
                ))}
              </div>

              {/* Footer row */}
              <div className="flex items-center justify-between pt-4 border-t-2 border-transparent flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <span className="text-cl-dark font-black text-lg">£{booking.total.toFixed(2)}</span>
                  {booking.wasteNote && (
                    <span className="flex items-center gap-1.5 text-gray-400 text-xs font-semibold">
                      <i className="ri-file-text-line"></i>
                      {booking.wasteNote}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {booking.status === "completed" && (
                    <a
                      href={booking.receiptUrl}
                      className="flex items-center gap-1.5 text-xs font-bold text-cl-green hover:underline cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-line"></i>
                      Receipt
                    </a>
                  )}
                  {booking.status === "upcoming" && (
                    <button className="flex items-center gap-1.5 text-xs font-bold text-red-500 hover:underline cursor-pointer whitespace-nowrap">
                      <i className="ri-close-circle-line"></i>
                      Cancel
                    </button>
                  )}
                  <Link
                    to="/book"
                    className="bg-cl-lime text-cl-dark font-black text-xs px-4 py-2 rounded-lg cartoon-btn whitespace-nowrap"
                  >
                    Book Same Service
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
